import { FormComponentBuilder } from 'components/common/form/layout';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import React, { useEffect, useState } from 'react';
import { alpha, Box, Button, Stack } from '@mui/material';
import useBundleTranslation from 'i18n';
import { useFormState } from 'react-hook-form';
import { instance } from 'api/api';
import { APIResponse, AssocArray } from 'tools/types';
import { AxiosResponse } from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useHasTokenString, ITokenString } from 'hooks/useHasTokenString';
import { tokenFormUpdatePropertyValue } from 'tools/tools';

export interface PluginGetTokenProps extends FormControlProps {
    buttonText: string;
    infoMessage?: string;
    windowUrl: string;
    windowTitle: string;
    settingsUrl: string;
    tokenField: string;
}

export default function PluginGetToken({ controlProps }: FormElementControlPropsType<PluginGetTokenProps>) {
    const { t } = useBundleTranslation(['components/common/get_plugin_token']);
    const [searchParams, setSearchParams] = useSearchParams();

    const baseUrl = process.env.REACT_APP_BACKEND_API_BASE_URL?.length
        ? process.env.REACT_APP_BACKEND_API_BASE_URL + '/'
        : '/';

    const [windowTitle, setWindowTitle] = useState(controlProps.windowTitle);
    const [windowUrl, setWindowUrl] = useState(controlProps.windowUrl);
    const [buttonText, setButtonText] = useState(controlProps.buttonText);
    const [infoMessage, setInfoMessage] = useState(controlProps.infoMessage);
    const tokenData: ITokenString = useHasTokenString(controlProps.tokenField);
    const { isDirty } = useFormState({ control: controlProps.form.hookFormControl });
    const handleClick = () => {
        const openWindow = () => {
            window.open(baseUrl + windowUrl, windowTitle, 'width=530,height=530');
        };

        if (isDirty) {
            controlProps.form.formSave().then(() => openWindow());
        } else {
            openWindow();
        }
    };

    useEffect(() => {
        if (controlProps.settingsUrl && -1 !== controlProps.settingsUrl.indexOf('user_token=Y')) {
            //@ts-ignore
            window.tokenFormUpdatePropertyValue = () => {
                const redirect_url: string =
                    //@ts-ignore
                    searchParams.has('redirect_url') && searchParams.get('redirect_url') > ''
                        ? (searchParams.get('redirect_url') ?? '')
                        : '';

                if (redirect_url > '') document.location = redirect_url;
                else document.location.reload();
            };
        } else {
            //@ts-ignore
            window.tokenFormUpdatePropertyValue = tokenFormUpdatePropertyValue;
        }

        return () => {
            //@ts-ignore
            delete window.tokenFormUpdatePropertyValue;
        };
    }, []);

    const [isLoading, setIsLoading] = useState(controlProps.settingsUrl != '');
    useEffect(() => {
        if (!controlProps.settingsUrl) {
            return;
        }
        instance
            .get(controlProps.settingsUrl)
            .then((response: AxiosResponse<APIResponse<any>>) => {
                if (response.data.status == 'OK') {
                    setWindowTitle(response.data.data.windowTitle);
                    setWindowUrl(response.data.data.windowUrl);
                    setButtonText(response.data.data.buttonText);
                    setInfoMessage(response.data.data?.infoMessage ?? '');
                }
            })
            .finally(() => setIsLoading(false));
    }, [controlProps.settingsUrl]);

    if (isLoading) {
        return null;
    }

    return (
        <Box>
            {undefined !== infoMessage && infoMessage > '' && (
                <Box sx={{ pb: 3 }}>
                    {infoMessage.split('\\n').map(function (item: any, key: any) {
                        return <Box key={key}>{item}</Box>;
                    })}
                </Box>
            )}
            <Stack direction="row" alignItems={'center'} sx={{ display: 'inline-flex' }}>
                <Button sx={{ mr: 1 }} type="button" variant="outlined" color="neutral" onClick={handleClick}>
                    {t(buttonText)}
                </Button>
                {tokenData.hasToken && (
                    <>
                        {tokenData.tokenFor.length > 0 ? (
                            <>
                                <Box sx={{ mr: 0.5, color: (theme) => alpha(theme.palette.text.primary, 0.64) }}>
                                    {t('token_generated_for')}
                                </Box>
                                <Box
                                    sx={{
                                        lineHeight: '24px',
                                        padding: '0px 6px',
                                        borderRadius: '3px',
                                        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                    }}
                                >
                                    {tokenData.tokenFor}
                                </Box>
                            </>
                        ) : (
                            <Box sx={{ color: (theme) => alpha(theme.palette.text.primary, 0.64) }}>
                                {t('token_generated')}
                            </Box>
                        )}
                    </>
                )}
            </Stack>
        </Box>
    );
}

export class PluginGetTokenBuilder extends FormComponentBuilder {
    prepareProps(): PluginGetTokenProps {
        return {
            ...this.controlProps,
            buttonText: this.elementProps.componentProps?.buttonText ?? '',
            infoMessage: this.elementProps.componentProps?.infoMessage ?? '',
            windowUrl: this.elementProps.componentProps?.windowUrl ?? '',
            windowTitle: this.elementProps.componentProps?.windowTitle ?? '',
            settingsUrl: this.elementProps.componentProps?.settingsUrl ?? '',
            tokenField: this.elementProps.componentProps?.tokenField ?? '',
        };
    }
}
